import styled from '@emotion/styled';

import theme from 'theme';
import {Placement, SizeTokenValue} from 'common';
import {ArrowDownIcon, ArrowUpIcon} from 'icons';
import {Tooltip} from 'Tooltip';
import {Dropdown as BaseDropdown} from 'Dropdown';

import {useDropdown} from './hook';

import type {DropdownListItemProps, DropdownProps, ListItemStyle} from './types';

const Container = styled.div(theme.typography.base);

const DropdownButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  height: 32px;
  gap: 6px;
  padding: 0 10px;
  text-transform: capitalize;
  text-align: left;
  color: ${theme.colors.grey[100]};
  background: none;
  border-style: solid;
  border-width: 2px;
  border-radius: 4px;
  border-color: transparent;
  cursor: pointer;

  ${theme.typography.p1}

  :hover {
    border-color: ${theme.colors.grey[40]};
  }

  &[data-open='true'] {
    border-color: ${theme.colors.accent[90]};
  }

  & svg {
    color: ${theme.colors.grey[60]};
  }
`;

const List = styled.div`
  width: 100%;
  max-height: 240px;
  padding-top: 8px;
  background-color: ${theme.colors.white};
  border-radius: 8px;
  box-shadow: ${theme.shadows.medium};
  overflow-y: auto;
  overflow-x: hidden;
  z-index: ${theme.zIndex.dropdown};

  &[hidden] {
    display: none;
  }

  ${theme.scroll}
`;

const ListItem = styled.button<ListItemStyle>`
  display: block;
  min-width: 100%;
  height: 32px;
  padding: 0 12px;
  text-transform: capitalize;
  text-align: left;
  background: none;
  border: none;
  outline: none;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};

  ${theme.typography.p1}

  :hover {
    color: ${theme.colors.grey[100]};
    background-color: ${theme.colors.grey[20]};
  }

  ${props => {
    if (props.isSelected) {
      return {
        color: theme.colors.white,
        backgroundColor: theme.colors.accent[90],
      };
    }
  }}
`;

const DropdownListItem: React.FC<DropdownListItemProps> = ({onChange, item, isSelected}) => {
  const {name, disabled} = item;

  return (
    <ListItem type="button" onClick={() => onChange(item)} disabled={disabled} isSelected={isSelected}>
      {name}
    </ListItem>
  );
};

export const Dropdown: React.FC<DropdownProps> = props => {
  const {items, value, isOpen, setIsOpen, tooltip, width, qa} = props;
  const {ref, isTooltipOpen, onToggle, onClickContainer, onClickItem} = useDropdown(props);

  const listItems = items.map(item => (
    <DropdownListItem key={item.value} item={item} isSelected={item.value === value.value} onChange={onClickItem} />
  ));

  const Chevron = isOpen ? ArrowUpIcon : ArrowDownIcon;

  const dropdownProps = {
    isOpen,
    setIsOpen,
    width,

    targetRef: ref,
    placement: Placement.BottomStart,
  };

  return (
    <Container data-qa={qa} onClick={onClickContainer}>
      <Tooltip placement={Placement.Bottom} content={tooltip} isOpen={isTooltipOpen}>
        <DropdownButton ref={ref} type="button" onClick={onToggle} data-open={isOpen}>
          {value.name}
          <Chevron size={SizeTokenValue.Small} />
        </DropdownButton>
      </Tooltip>

      <BaseDropdown {...dropdownProps}>
        <List>{listItems}</List>
      </BaseDropdown>
    </Container>
  );
};
